import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineCash, HiOutlineClock, HiOutlineDuplicate, HiOutlineLockClosed } from 'react-icons/hi';
import { RiCoinLine } from 'react-icons/ri';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { getErc20Balance, sendToken, shortAddress } from '../services/wallet';
import NumberInput, { parseNumberInput } from '../components/NumberInput';
import Modal from '../components/Modal';
import { PlansSkeleton, SkeletonTable } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

function money(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0.00';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function returnFor(amount, percent) {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Number(((amount * Number(percent)) / 100).toFixed(8));
}

function txUrl(chainId, hash) {
  const host = Number(chainId) === 97 ? 'https://testnet.bscscan.com' : 'https://bscscan.com';
  return `${host}/tx/${hash}`;
}

const STATUS_LABEL = {
  active: 'locked',
  claiming: 'claiming…',
  claimed: 'claimed',
  rejected: 'rejected',
};

function upsert(list, row) {
  const rest = list.filter((item) => item.id !== row.id);
  return [row, ...rest].sort((a, b) => Number(b.id) - Number(a.id));
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}

export default function StakingPage() {
  const { config, user, address, requireAuth, connectOpening, loading } = useWallet();
  const toast = useToast();
  const [slabs, setSlabs] = useState(null);
  const [slabId, setSlabId] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [flow, setFlow] = useState('');
  const [flowError, setFlowError] = useState('');
  const [manualHash, setManualHash] = useState('');
  const [claimingId, setClaimingId] = useState(null);
  const [dfiBalance, setDfiBalance] = useState(null);
  const notified = useRef('');

  const min = Number(config?.stake_min_dfi);
  const max = Number(config?.stake_max_dfi);
  const wallet = user?.wallet_address || address;

  useEffect(() => {
    let active = true;
    api.stakingSlabs()
      .then((data) => {
        if (!active) return;
        const list = data.slabs || [];
        setSlabs(list);
        setSlabId((current) => (list.some((s) => s.id === current) ? current : list[0]?.id ?? null));
      })
      .catch(() => {
        if (active) setSlabs([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const loadRecords = useCallback(() => {
    return api.myStakes().then((data) => setRecords(data.stakes || []));
  }, []);

  useEffect(() => {
    if (!user) {
      setRecords([]);
      setCheckout(null);
      return;
    }
    setRecordsLoading(true);
    loadRecords()
      .catch(() => {})
      .finally(() => setRecordsLoading(false));
  }, [user, loadRecords]);

  const refreshBalance = useCallback(() => {
    if (!wallet || !config?.dfi_contract) return;
    getErc20Balance(config.dfi_contract, wallet)
      .then((data) => setDfiBalance(data.balance))
      .catch(() => setDfiBalance(null));
  }, [wallet, config?.dfi_contract]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  // Re-render each 30s so stakes reaching their end date show the Claim button without a reload
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  const pendingId = checkout?.status === 'pending' ? checkout.id : null;

  useEffect(() => {
    if (!pendingId) return undefined;
    const timer = setInterval(() => {
      api.stakeStatus(pendingId)
        .then((data) => {
          const next = data.stake;
          if (!next) return;
          setRecords((current) => upsert(current, next));
          setCheckout(next);
          if (next.status === 'pending' || next.status === notified.current) return;
          notified.current = next.status;
          // Only switch the modal if it is still open; a closed modal just gets the toast
          if (next.status === 'active') {
            setFlow((current) => (current ? 'success' : current));
            refreshBalance();
            toast.success(`Stake locked until ${new Date(next.matures_at).toLocaleString()}`);
          } else if (next.status === 'rejected') {
            setFlowError(next.note || 'The DFI transfer could not be verified.');
            setFlow((current) => (current ? 'rejected' : current));
            toast.error(next.note || 'The DFI transfer could not be verified.');
          }
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [pendingId, toast, refreshBalance]);

  if (loading || !config || !slabs) {
    return <PlansSkeleton />;
  }

  const slab = slabs.find((s) => s.id === slabId) || null;
  const selected = parseNumberInput(amount);
  const previewReturn = slab ? returnFor(selected, slab.return_percent) : 0;
  const lockedTotal = records
    .filter((row) => row.status === 'active' || row.status === 'matured' || row.status === 'claiming')
    .reduce((sum, row) => sum + Number(row.amount_dfi || 0), 0);

  function openCheckout(stake) {
    notified.current = '';
    setCheckout(stake);
    setManualHash('');
    setFlowError('');
    setFlow(stake.tx_hash ? 'verifying' : 'transfer');
  }

  async function stake() {
    if (!slab) {
      toast.error('Choose a staking period');
      return;
    }
    const value = parseNumberInput(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a DFI amount');
      return;
    }
    if (value < min || value > max) {
      toast.error(`Amount must be between ${min} and ${max} DFI`);
      return;
    }

    setBusy(true);
    setFlow('fetching');
    setFlowError('');
    try {
      const data = await api.startStake(slab.id, value);
      const next = data.stake;
      if (!next?.deposit_address) {
        throw new Error('Deposit address was not returned');
      }
      setRecords((current) => upsert(current, next));
      setAmount('');
      openCheckout(next);
    } catch (error) {
      setFlowError(error.message || 'The stake could not be opened.');
      setFlow('error');
    } finally {
      setBusy(false);
    }
  }

  async function reportHash(id, hash) {
    const data = await api.submitStakeTx(id, hash);
    if (data.stake) {
      setCheckout(data.stake);
      setRecords((current) => upsert(current, data.stake));
    }
  }

  async function sendWithWallet() {
    if (!checkout) return;
    setFlow('approve');
    setFlowError('');
    try {
      await sendToken({
        tokenContract: config.dfi_contract,
        to: checkout.deposit_address,
        amount: Number(checkout.amount_dfi),
        symbol: 'DFI',
        onSubmitted: (hash) => {
          setFlow('verifying');
          return reportHash(checkout.id, hash);
        },
      });
      setFlow((current) => (current === 'approve' ? 'verifying' : current));
      refreshBalance();
      toast.success('DFI sent. Verifying the transfer on BscScan…');
    } catch (error) {
      const cancelled = error.code === 4001;
      setFlowError(error.message || 'The transfer could not be sent.');
      setFlow((current) => (current === 'approve' ? 'transfer' : current));
      if (cancelled) toast.info(error.message);
      else toast.error(error.message);
    }
  }

  async function submitManualHash(event) {
    event.preventDefault();
    if (!checkout) return;
    const hash = manualHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(hash)) {
      setFlowError('Enter the 66-character transaction hash that starts with 0x.');
      return;
    }
    setFlowError('');
    try {
      await reportHash(checkout.id, hash);
      setFlow('verifying');
    } catch (error) {
      setFlowError(error.message);
    }
  }

  async function claim(row) {
    setClaimingId(row.id);
    try {
      const data = await api.claimStake(row.id);
      if (data.stake) setRecords((current) => upsert(current, data.stake));
      refreshBalance();
      toast.success(`${money(row.payout_dfi)} DFI sent to your wallet`);
    } catch (error) {
      toast.error(error.message || 'The claim could not be completed.');
      loadRecords().catch(() => {});
    } finally {
      setClaimingId(null);
    }
  }

  function closeFlow() {
    if (flow === 'fetching' || flow === 'approve') return;
    setFlow('');
    setFlowError('');
  }

  function statusCell(row) {
    if (row.status === 'pending') {
      return (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => openCheckout(row)}>
          {row.tx_hash ? 'Verifying…' : 'Send DFI'}
        </button>
      );
    }
    if (row.status === 'matured') {
      const claiming = claimingId === row.id;
      return (
        <>
          <button
            type="button"
            className={`btn btn--primary btn--sm${claiming ? ' is-busy' : ''}`}
            disabled={Boolean(claimingId)}
            aria-busy={claiming}
            onClick={() => claim(row)}
          >
            {claiming ? <span className="btn__spin" aria-hidden /> : null}
            {claiming ? 'Claiming…' : 'Claim'}
          </button>
          {row.claim_error ? <span className="stake-row__note">Last try failed. Please claim again.</span> : null}
        </>
      );
    }
    if (row.status === 'claimed' && row.claim_tx_hash) {
      return (
        <a className="badge" href={txUrl(config.chain_id, row.claim_tx_hash)} target="_blank" rel="noreferrer">
          claimed
        </a>
      );
    }
    return (
      <>
        <span className={`badge${row.status === 'active' || row.status === 'claimed' ? '' : ' badge--pending'}`}>
          {STATUS_LABEL[row.status] || row.status}
        </span>
        {row.status === 'rejected' && row.note ? <span className="stake-row__note">{row.note}</span> : null}
      </>
    );
  }

  const flowDone = flow === 'success' || flow === 'error' || flow === 'rejected';
  const opening = flow === 'fetching' || flow === 'approve';

  const titles = {
    fetching: 'Preparing deposit',
    transfer: 'Transfer DFI',
    approve: 'Approve the transfer',
    verifying: 'Verifying on BscScan',
    success: 'Stake started',
    rejected: 'Stake rejected',
    error: 'Stake was not opened',
  };
  const subtitles = {
    fetching: 'Getting a deposit address for this stake.',
    transfer: checkout ? `Send exactly ${money(checkout.amount_dfi)} DFI (BEP-20) to the address below.` : undefined,
    approve: checkout ? `Confirm ${money(checkout.amount_dfi)} DFI in your wallet.` : undefined,
    verifying: 'The stake starts once BscScan shows the transfer with 3 confirmations. You can close this window.',
    success: checkout?.matures_at ? `Your DFI is locked until ${new Date(checkout.matures_at).toLocaleString()}.` : undefined,
  };

  return (
    <>
      <PageHeader title="Staking" subtitle="Lock DFI for a fixed period and claim it back with a reward" />
      <div className="panel-grid">
        <motion.section
          className="panel panel--glow panel--tint-violet"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Stake · Mature · Claim</div>
          <h2>Choose a staking period</h2>
          <p className="muted" style={{ maxWidth: '36rem', lineHeight: 1.55 }}>
            Your DFI is locked for the period you pick. When it ends, claim the amount plus its reward straight to your
            wallet in DFI.
          </p>

          {slabs.length ? (
            <div className="stake-slabs" role="radiogroup" aria-label="Staking period">
              {slabs.map((item) => {
                const active = item.id === slabId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`stake-slab${active ? ' is-active' : ''}`}
                    onClick={() => setSlabId(item.id)}
                    disabled={busy}
                  >
                    <span className="stake-slab__days">
                      <HiOutlineClock aria-hidden="true" />
                      {item.days} days
                    </span>
                    <strong className="stake-slab__rate">
                      {Number(item.return_percent)}
                      <em>%</em>
                    </strong>
                    <span className="stake-slab__hint">reward</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="muted" style={{ marginTop: '1rem' }}>No staking periods are open right now.</p>
          )}

          <div className="plans-meta-grid" style={{ marginTop: '1rem' }}>
            <div className="stat dash-tile dash-tile--cyan">
              <span>
                <HiOutlineCash style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Stake range
              </span>
              <strong>
                {Number.isFinite(min) ? min : '—'}
                <em>– {Number.isFinite(max) ? max : '—'} DFI</em>
              </strong>
            </div>
            <div className="stat dash-tile dash-tile--violet">
              <span>
                <HiOutlineLockClosed style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Currently locked
              </span>
              <strong>
                {money(lockedTotal)}
                <em>DFI</em>
              </strong>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="panel panel--tint-gold"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3>Stake</h3>
          <div className="field">
            <label htmlFor="stake-amount">DFI amount</label>
            <NumberInput
              id="stake-amount"
              value={amount}
              onChange={setAmount}
              allowDecimal
              maxDecimals={6}
              placeholder={Number.isFinite(min) ? `${min} – ${max}` : 'Enter amount'}
            />
            {user && dfiBalance != null ? (
              <span className="muted" style={{ fontSize: '0.78rem' }}>Wallet balance: {money(dfiBalance)} DFI</span>
            ) : null}
          </div>
          {slab && Number.isFinite(selected) && selected > 0 ? (
            <div className="stake-preview">
              <div>
                <span>Reward ({Number(slab.return_percent)}%)</span>
                <strong>+{money(previewReturn)} DFI</strong>
              </div>
              <div>
                <span>You claim after {slab.days} days</span>
                <strong>{money(selected + previewReturn)} DFI</strong>
              </div>
            </div>
          ) : null}
          <motion.button
            className={`btn btn--primary${connectOpening && !user ? ' is-busy' : ''}`}
            type="button"
            disabled={busy || opening || !slab || (connectOpening && !user)}
            aria-busy={opening || (connectOpening && !user)}
            onClick={() => requireAuth(stake, 'Connect your wallet to stake')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {connectOpening && !user ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
            {flow === 'fetching' ? 'Getting address…' : connectOpening && !user ? 'Connecting…' : user ? 'Stake DFI' : 'Connect & Stake'}
          </motion.button>
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Staked DFI cannot be withdrawn before the period ends. When it ends, use Claim in your stake history to
            receive the DFI in your wallet.
          </p>
        </motion.section>
      </div>

      <motion.section
        className="panel"
        style={{ marginTop: '1rem' }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
      >
        <h3>
          <RiCoinLine style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Stake history
        </h3>
        {recordsLoading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Amount</th>
                  <th>Period</th>
                  <th>Payout</th>
                  <th>Matures</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td>
                      {row.tx_hash && row.status !== 'pending' ? (
                        <a href={txUrl(config.chain_id, row.tx_hash)} target="_blank" rel="noreferrer">
                          {money(row.amount_dfi)} DFI
                        </a>
                      ) : (
                        `${money(row.amount_dfi)} DFI`
                      )}
                    </td>
                    <td>{row.days} days · {Number(row.return_percent)}%</td>
                    <td>{money(row.payout_dfi)} DFI</td>
                    <td>{row.matures_at ? new Date(row.matures_at).toLocaleString() : '—'}</td>
                    <td className="stake-row__status">{statusCell(row)}</td>
                  </tr>
                ))}
                {!records.length ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      {user ? 'No stakes yet.' : 'Connect your wallet to see your stakes.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>

      <Modal
        open={Boolean(flow)}
        onClose={opening ? undefined : closeFlow}
        eyebrow="Stake deposit"
        title={titles[flow]}
        subtitle={subtitles[flow]}
        footer={
          flowDone ? (
            <button type="button" className="btn btn--primary" onClick={closeFlow}>
              Close
            </button>
          ) : flow === 'transfer' ? (
            <button type="button" className="btn btn--primary btn--block" onClick={sendWithWallet}>
              <TbWallet />
              Send {checkout ? money(checkout.amount_dfi) : ''} DFI with wallet
            </button>
          ) : null
        }
      >
        {flow === 'fetching' || flow === 'approve' || flow === 'verifying' ? (
          <div className="modal__connecting">
            <div className="modal__spinner" />
            <p className="modal__busy">
              {flow === 'fetching'
                ? 'Requesting the deposit address…'
                : flow === 'approve'
                  ? 'Waiting for wallet approval…'
                  : 'Checking the transfer on BscScan…'}
            </p>
            {flow === 'verifying' && checkout?.tx_hash ? (
              <a href={txUrl(config.chain_id, checkout.tx_hash)} target="_blank" rel="noreferrer">
                View transaction
              </a>
            ) : null}
          </div>
        ) : null}

        {flow === 'transfer' && checkout ? (
          <div className="stake-transfer">
            <div className="stake-preview">
              <div>
                <span>Amount</span>
                <strong>{money(checkout.amount_dfi)} DFI</strong>
              </div>
              <div>
                <span>Period</span>
                <strong>{checkout.days} days · {Number(checkout.return_percent)}%</strong>
              </div>
              <div>
                <span>From</span>
                <strong>{shortAddress(wallet)}</strong>
              </div>
            </div>
            <label className="stake-transfer__label" htmlFor="stake-deposit-address">Deposit address</label>
            <div className="stake-transfer__address">
              <code id="stake-deposit-address">{checkout.deposit_address}</code>
              <button
                type="button"
                aria-label="Copy deposit address"
                onClick={() => copyText(checkout.deposit_address).then((ok) => ok && toast.success('Address copied'))}
              >
                <HiOutlineDuplicate />
              </button>
            </div>
            <p className="muted stake-transfer__hint">
              Send DFI only, from your connected wallet, on BNB Smart Chain.
              {checkout.expires_at ? ` Send before ${new Date(checkout.expires_at).toLocaleTimeString()}.` : ''}
            </p>
            <form className="stake-transfer__manual" onSubmit={submitManualHash}>
              <label htmlFor="stake-tx-hash">Already sent it? Paste the transaction hash</label>
              <div>
                <input
                  id="stake-tx-hash"
                  value={manualHash}
                  onChange={(event) => setManualHash(event.target.value.trim())}
                  placeholder="0x…"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button type="submit" className="btn btn--ghost btn--sm">Verify</button>
              </div>
            </form>
          </div>
        ) : null}

        {flowError && (flow === 'transfer' || flow === 'error' || flow === 'rejected') ? (
          <p className="modal__error">{flowError}</p>
        ) : null}
      </Modal>
    </>
  );
}
